import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Tooltip } from '@mui/material';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useNotify } from 'react-admin';
import { DEFAULT_API_ADMIN } from '../../constants';
import { httpClientReactAdmin } from '../../lib';

/**
 * Preenche o cadastro do posto com os dados da Receita Federal a partir do CNPJ.
 *
 * Razão social, inscrição estadual e endereço oficial não são digitados de forma confiável — em
 * 1.291 postos, razão social estava preenchida em 93 e inscrição estadual em 82. E o Gestão Click
 * precisa dos três para emitir nota.
 *
 * Campo já preenchido não é sobrescrito: o que o time digitou vale mais que o cadastro da Receita
 * (o nome de exibição do posto, por exemplo, costuma ser o nome de fantasia).
 */
export const CnpjLookupButton = () => {
  const notify = useNotify();
  const { setValue, getValues } = useFormContext();
  const cnpj = useWatch({ name: 'cnpj' });
  const [loading, setLoading] = useState(false);

  const digits = String(cnpj || '').replace(/\D/g, '');
  const canSearch = digits.length === 14;

  const fill = (field, value) => {
    if (!value) return false;
    const current = getValues(field);
    if (current !== undefined && current !== null && String(current).trim() !== '') return false;
    setValue(field, value, { shouldDirty: true });
    return true;
  };

  const search = async () => {
    setLoading(true);
    try {
      // O parser de query do backend faz JSON.parse: CNPJ sem zero à esquerda virava number
      // e o Joi (string) devolvia 400. Entre aspas ele chega sempre como string.
      const { json } = await httpClientReactAdmin(
        `${DEFAULT_API_ADMIN}/cnpj-lookup?cnpj=${encodeURIComponent(JSON.stringify(digits))}`
      );
      const address = json.address || {};

      const filled = [
        fill('corporateName', json.corporateName),
        fill('stateRegistration', json.stateRegistration),
        fill('cep', String(address.zipCode || '').replace(/\D/g, '')),
        fill('street', address.street),
        fill('streetNumber', address.number),
        fill('complement', address.complement),
        fill('neighbourhood', address.neighborhood),
        fill('city', address.city),
        fill('state', address.state),
      ].filter(Boolean).length;

      if (json.status && json.status !== 'Ativa') {
        notify(`CNPJ com situação "${json.status}" na Receita`, { type: 'warning' });
      } else if (filled === 0) {
        notify('Nada a preencher — os campos já estão preenchidos', { type: 'info' });
      } else {
        notify(`${filled} campo(s) preenchido(s) com os dados da Receita`, { type: 'success' });
      }
    } catch (error) {
      notify(
        error?.body?.message || error?.message || 'Não foi possível consultar o CNPJ',
        { type: 'warning' }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <Tooltip title={canSearch ? 'Preenche razão social, inscrição estadual e endereço' : 'Informe o CNPJ completo'}>
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<SearchIcon />}
            onClick={search}
            disabled={!canSearch || loading}
          >
            {loading ? 'Buscando...' : 'Buscar na Receita'}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
};

export default CnpjLookupButton;
